import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, HelpCircle, Mail, Check, GraduationCap, Building2, BarChart3, Calculator, Image as ImageIcon, Download } from "lucide-react";
import { useEffect } from "react";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import { Squiggle, SquiggleUnderline, RouteLine, FloatingBlob } from "@/components/Squiggle";
import usePageMeta from "@/hooks/usePageMeta";
import { usePerspective } from "@/context/PerspectiveContext";
import { buildPilotMailto, ASSET_BROCHURE_URL, ASSET_VIGOR_URL, ASSET_FLYER_URL, ASSET_FLYER_IMG, ASSET_QA_URL } from "@/lib/mailto";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const ontbrekendeFase = [
  {
    n: "01",
    t: "Wat nu wél gebeurt",
    d: "Wijkteam, welzijn, re-integratie, SLB, LOB, zorgteams. Allemaal noodzakelijk. Allemaal gericht op wat iemand kan of moet.",
  },
  {
    n: "02",
    t: "Waar het spaak loopt",
    d: "Voor een deel van uw bestand ontbreekt de fase dáárvoor: de mens weer in beweging krijgen, ontdekken wat wél werkt.",
  },
  {
    n: "03",
    t: "Wat LevelLeven doet",
    d: "Niet een extra traject, maar de menselijke ontwikkelfase die zorgt dat wat ú aanbiedt daarna écht landt.",
  },
];

const resets = [
  {
    icon: Building2,
    naam: "Reset voor gemeenten",
    duur: "12 weken · 20–22 deelnemers",
    voor: "Inwoners in de bijstand die al langere tijd stilstaan.",
    kern:
      "LevelLeven START duurt twaalf weken, met 20–22 deelnemers (max. 24) en twee trainers. Start-, tussen- en eindmeting, en een warme overdracht naar een passende vervolgstap. VERDER is een mogelijk vervolg, geen automatische voortzetting.",
    to: "/gemeenten",
    kleur: "coral",
  },
  {
    icon: GraduationCap,
    naam: "Student Reset",
    duur: "12 weken",
    voor: "Mbo- en hbo-studenten die dreigen uit te vallen of moeten heroriënteren.",
    kern:
      "Twaalf weken, twee keer drie uur per week. Individuele begeleiding erbij. Sluit aan op SLB, LOB, decanen, zorgteams en Doorstroompunt.",
    to: "/onderwijs",
    kleur: "sky",
  },
];

const bewijs = [
  { l: "Nulmeting", v: "T=0", s: "Waar staat iemand nu — in eigen woorden en met vragenlijst." },
  { l: "Tussenmeting", v: "T=6", s: "Wat is er in beweging gekomen. Wat blijft steken." },
  { l: "Eindmeting", v: "T=12", s: "Wat lukt er nu wat aan het begin nog niet lukte." },
  { l: "Follow-up", v: "T=18", s: "Houdt de vervolgstap stand — een half jaar later." },
];

const aanpakZij = [
  "Wijkteams · welzijn · re-integratie",
  "SLB · LOB · decanen · zorgteams",
  "Werkgevers · maatschappelijke partners",
  "Onderzoekspartners · universiteiten",
];

const downloads = [
  { icon: FileText, label: "Brochure (PDF)", hint: "Het volledige programma", href: ASSET_BROCHURE_URL },
  { icon: FileText, label: "LevelLeven × VIGOR (PDF)", hint: "Van beweging naar geborgde skills", href: ASSET_VIGOR_URL },
  { icon: ImageIcon, label: "One-pager (PNG)", hint: "Het hele verhaal op één A4", href: ASSET_FLYER_URL },
  { icon: HelpCircle, label: "Vraag & antwoord", hint: "Alle FAQ's op één plek", href: ASSET_QA_URL },
  {
    icon: Mail,
    label: "Pilotvoorstel op maat",
    hint: "Vraag rechtstreeks aan",
    href: buildPilotMailto({
      subject: "Aanvraag pilotvoorstel LevelLeven",
      body:
        "Beste LevelLeven,\n\nGraag ontvangen wij een pilotvoorstel op maat.\n\nOrganisatie:\nContactpersoon:\nFunctie:\nTelefoon:\n\nMet vriendelijke groet,",
    }),
  },
];

export default function VoorGemeenten() {
  usePageMeta({
    title: "Voor opdrachtgevers · LevelLeven",
    description:
      "Voor sommige inwoners komt een nieuw re-integratietraject te vroeg. LevelLeven START gebruikt twaalf weken om eerst uit te zoeken wat beweging tegenhoudt en wat wél werkt.",
  });

  const { setPerspective } = usePerspective();
  useEffect(() => {
    setPerspective("opdrachtgever");
  }, [setPerspective]);

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO */}
      <SpeelruimteBanner
        eyebrow="Voor opdrachtgevers"
        headline={
          <>
            Waar ontstaat{" "}
            <span className="italic text-ll-coral relative inline-block">
              aantoonbare
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>
            <br />
            beweging?
          </>
        }
        belofte="De volgende beweging begint klein — meetbaar, in fase."
        subhead="LevelLeven is geen extra re-integratietraject. Het is de ontbrekende ontwikkelfase daarvóór: de mens weer in beweging brengen zodat wat u daarna aanbiedt écht kan landen. Aanvullend op wijkteams, welzijn, re-integratie, SLB en LOB."
        metrics={[
          { label: "Aanpak", value: "Aanvullend", sub: "Geen parallel systeem" },
          { label: "Meting", value: "T=0 · 6 · 12", sub: "Nul-, tussen- en eindmeting" },
          { label: "Overdracht", value: "Warm", sub: "Met u en uw ketenpartners" },
        ]}
        ctaPrimary={{ label: "Plan een verkennend gemeentegesprek", href: buildPilotMailto(), testid: "voorgem-cta-pilot" }}
        ctaSecondary={{ label: "Download brochure", href: ASSET_BROCHURE_URL, external: true }}
        images={HERO_IMGS}
        tone="sky"
      />

      {/* DE ONTBREKENDE FASE */}
      <section className="py-24 sm:py-32 relative overflow-hidden">
        <FloatingBlob color="#E06B3F" size={160} className="-top-10 -right-16 opacity-70" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">De ontbrekende fase</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              We vervangen niet wat er al is.{" "}
              <span className="italic text-ll-coral">We helpen het beter te laten werken.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {ontbrekendeFase.map((f, i) => (
              <motion.div
                key={f.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="border-t border-ll-ink/20 pt-8 relative"
              >
                <span className="chapter-num text-4xl text-ll-coral">{f.n}</span>
                <h3 className="mt-6 font-heading text-2xl font-light text-ll-ink">{f.t}</h3>
                <p className="mt-3 text-ll-navy/80 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-16">
            <RouteLine steps={["Wijk", "LevelLeven", "Re-integratie", "Werk"]} accent="#E06B3F" />
          </div>
        </div>
      </section>

      {/* TWEE RESETS */}
      <section className="py-24 sm:py-32 bg-ll-navy text-ll-cream">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">Twee resets · één methodiek</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light text-ll-cream">
              Voor wie stilstaat.{" "}
              <span className="italic text-ll-coral">In de wijk óf op school.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resets.map((r, i) => {
              const accent = r.kleur === "sky" ? "#97B79D" : "#E06B3F";
              return (
                <motion.div
                  key={r.naam}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.12, duration: 0.7 }}
                  viewport={{ once: true }}
                  className="rounded-3xl border border-ll-cream/10 p-10 bg-ll-cream/[0.02] hover:bg-ll-cream/[0.06] transition-colors"
                >
                  <r.icon size={32} strokeWidth={1.2} style={{ color: accent }} />
                  <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-ll-cream/60">{r.duur}</p>
                  <h3 className="mt-3 font-heading text-3xl font-light text-ll-cream">{r.naam}</h3>
                  <p className="mt-4 italic text-ll-cream/70">{r.voor}</p>
                  <p className="mt-4 text-ll-cream/90 leading-relaxed">{r.kern}</p>
                  <Link
                    to={r.to}
                    data-testid={`voorgem-reset-${r.kleur}`}
                    className="mt-8 group inline-flex items-center gap-2 font-heading italic text-xl transition-colors"
                    style={{ color: accent }}
                  >
                    Zie de opzet
                    <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROGRAMMABOUWER — de motor voor de opdrachtgever */}
      <section className="py-20 sm:py-24 relative overflow-hidden" data-testid="voorgem-programmabouwer">
        <FloatingBlob color="#F0B98D" size={160} className="top-6 -right-10 opacity-70" />
        <FloatingBlob color="#97B79D" size={110} className="bottom-6 -left-8 opacity-50" delay={1.4} />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <Link
            to="/programmabouwer"
            data-testid="voorgem-programmabouwer-cta"
            className="group block rounded-[36px] border-2 border-ll-ink/15 p-10 sm:p-14 bg-white hover:bg-ll-coral hover:text-ll-cream hover:border-ll-coral transition-all"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-3">
                  <Calculator size={22} strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.35em]">De Programmabouwer</p>
                </div>
                <h3 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight font-light">
                  Bouw uw eigen{" "}
                  <span className="italic group-hover:not-italic transition-all">LevelLeven-programma</span>.
                </h3>
                <p className="mt-4 leading-relaxed max-w-xl group-hover:text-ll-cream/90">
                  Kies doelgroep en onderzoeksfocus en zie direct een indicatieve kostenraming. Een gesprekstool, geen offerte.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {["Student Reset", "Gemeenten START", "Gemeenten VERDER"].map((t) => (
                    <span key={t} className="text-xs px-3 py-1 rounded-full border border-current/30">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <span className="inline-flex items-center gap-3 rounded-full bg-ll-ink text-ll-cream group-hover:bg-ll-cream group-hover:text-ll-ink pl-6 pr-2 py-2 transition-colors">
                  <span className="text-base font-medium">Start met bouwen</span>
                  <span className="bg-ll-coral group-hover:bg-ll-ink text-ll-cream w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                    <ArrowUpRight size={18} />
                  </span>
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ONDERZOEK & BEWIJS */}
      <section className="py-24 sm:py-32 relative">
        <FloatingBlob color="#F0B98D" size={130} className="top-20 -left-12 opacity-80" delay={1} />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Onderzoek & bewijs</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Geen gevoel.{" "}
              <span className="italic text-ll-coral">Feiten. In fase.</span>
            </h2>
            <p className="mt-8 text-lg text-ll-navy/80 leading-relaxed max-w-2xl">
              Elke pilot start met een nulmeting. Elke fase heeft een tussenmeting. Elke rapportage sluit aan op uw KPI's — participatiewet, welzijnsplan, of onderwijsagenda.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {bewijs.map((b, i) => (
              <motion.div
                key={b.l}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-white border border-ll-ink/10"
              >
                <BarChart3 size={22} className="text-ll-coral" strokeWidth={1.4} />
                <p className="mt-5 chapter-num text-3xl text-ll-ink">{b.v}</p>
                <p className="text-[10px] uppercase tracking-[0.28em] mt-1 text-ll-navy/60">{b.l}</p>
                <p className="mt-3 text-sm text-ll-navy/80">{b.s}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 max-w-3xl">
            <Link
              to="/onderzoek-en-onderbouwing"
              className="inline-flex items-center gap-2 text-lg font-heading italic text-ll-coral hover:text-ll-ink transition-colors"
              data-testid="voorgem-onderzoek-link"
            >
              Zie de volledige onderzoeksopzet
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOE WIJ AANVULLEND WERKEN */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Aanvullend, niet parallel</p>
              <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
                Naast wat er is.{" "}
                <span className="italic text-ll-coral">Niet ernaast.</span>
              </h2>
              <p className="mt-8 text-lg text-ll-navy/80 leading-relaxed">
                LevelLeven werkt met en náást uw bestaande keten. We nemen niets over, we vullen aan waar de menselijke ontwikkelfase ontbreekt.
              </p>
            </div>
            <div className="lg:col-span-7 lg:pl-12">
              <ul className="space-y-4">
                {aanpakZij.map((a, i) => (
                  <motion.li
                    key={a}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 pb-4 border-b border-ll-ink/15"
                  >
                    <Check size={20} className="text-ll-coral shrink-0" />
                    <span className="text-lg text-ll-ink">{a}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ONE-PAGER */}
      <section className="py-24 sm:py-32 relative overflow-hidden" data-testid="voorgem-onepager">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Op één A4</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Wat als re-integratie{" "}
              <span className="italic text-ll-coral">nog een stap te ver is?</span>
            </h2>
            <p className="mt-8 text-lg text-ll-navy/80 leading-relaxed">
              Het hele verhaal in één oogopslag. Handig om door te sturen naar een collega of mee te nemen naar het overleg.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={ASSET_FLYER_URL}
                download
                data-testid="voorgem-onepager-download"
                className="inline-flex items-center gap-2 bg-ll-coral hover:bg-ll-ink text-white rounded-full px-6 py-3 transition-colors"
              >
                <Download size={16} /> Download one-pager
              </a>
              <a
                href={ASSET_BROCHURE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="voorgem-onepager-brochure"
                className="inline-flex items-center gap-2 border border-ll-ink/25 hover:bg-ll-ink hover:text-ll-cream rounded-full px-6 py-3 transition-colors"
              >
                Lees de brochure <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
          <motion.a
            href={ASSET_FLYER_URL}
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 24, rotate: 0 }}
            whileInView={{ opacity: 1, y: 0, rotate: -1.5 }}
            whileHover={{ rotate: 0, scale: 1.01 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            data-testid="voorgem-onepager-image"
            className="lg:col-span-7 block max-w-xl mx-auto w-full rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(31,81,50,0.45)] border border-ll-ink/10 bg-white"
          >
            <img src={ASSET_FLYER_IMG} alt="One-pager LevelLeven: wat als re-integratie nog een stap te ver is?" loading="lazy" className="w-full h-auto block" />
          </motion.a>
        </div>
      </section>

      {/* DOWNLOADS + CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Klaar om te bespreken</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              Neem de eerste{" "}
              <span className="italic text-ll-coral">kleine beweging</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {downloads.map((d, i) => (
              <motion.a
                key={d.label}
                href={d.href}
                target={d.href.startsWith("mailto") ? undefined : "_blank"}
                rel="noreferrer"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                viewport={{ once: true }}
                data-testid={`voorgem-download-${i}`}
                className="group p-8 rounded-3xl border border-ll-ink/15 bg-white hover:bg-ll-ink hover:text-ll-cream transition-colors block"
              >
                <d.icon size={28} strokeWidth={1.4} />
                <h3 className="mt-6 font-heading text-2xl font-light">{d.label}</h3>
                <p className="mt-2 text-sm opacity-70">{d.hint}</p>
                <ArrowUpRight size={18} className="mt-6 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
